import type { PlanData, TaskNode } from "./types";
import { fmt } from "./format";

// A compact, human-readable diff between two plan snapshots (previous → this version).
// Used by the history dialog: most saves change little, so a short list reads well.

interface Flat { node: TaskNode; path: string; parent: string }

// map id → { node, breadcrumb path, parent path } with the lone root trimmed (as shown in the UI)
function flatten(tree: TaskNode[]): Map<string, Flat> {
  const solo = tree.length === 1;
  const map = new Map<string, Flat>();
  (function walk(list: TaskNode[], anc: string[]) {
    for (const n of list) {
      const names = anc.concat(n.name);
      const shown = solo && names.length > 1 ? names.slice(1) : names;
      map.set(n.id, { node: n, path: shown.join(" > "), parent: shown.slice(0, -1).join(" > ") });
      walk(n.children, names);
    }
  })(tree, []);
  return map;
}

function listDiff(label: string, a: string[], b: string[], lines: string[]): void {
  b.filter((x) => !a.includes(x)).forEach((x) => lines.push(`${label} added: ${x}`));
  a.filter((x) => !b.includes(x)).forEach((x) => lines.push(`${label} removed: ${x}`));
}

const estText = (n: number) => (n ? fmt(n) + "d" : "0d");

// per-task scalar field changes, prefixed with the task path for context
function fieldDiffs(prev: TaskNode, next: TaskNode, path: string, lines: string[]): void {
  // estimate: per-stream when named streams are in play, else the single total
  const streams = new Set([...Object.keys(prev.estimates || {}), ...Object.keys(next.estimates || {})]);
  let streamChanged = false;
  for (const s of streams) {
    const a = prev.estimates?.[s] || 0, b = next.estimates?.[s] || 0;
    if (a !== b) { lines.push(`${path}: ${s} ${estText(a)} → ${estText(b)}`); streamChanged = true; }
  }
  if (!streamChanged && prev.estimate !== next.estimate) lines.push(`${path}: estimate ${estText(prev.estimate)} → ${estText(next.estimate)}`);
  if ((prev.version || "") !== (next.version || "")) lines.push(`${path}: version ${prev.version || "—"} → ${next.version || "—"}`);
  if ((prev.component || "") !== (next.component || "")) lines.push(`${path}: component ${prev.component || "—"} → ${next.component || "—"}`);
  if ((prev.jiraKey || "") !== (next.jiraKey || "")) lines.push(`${path}: Jira key ${prev.jiraKey || "—"} → ${next.jiraKey || "—"}`);
  if (!!prev.done !== !!next.done) lines.push(`${path}: ${next.done ? "marked done" : "reopened"}`);
  if (!!prev.disabled !== !!next.disabled) lines.push(`${path}: ${next.disabled ? "disabled" : "enabled"}`);
  if ((prev.tag || "") !== (next.tag || "")) lines.push(`${path}: tag ${prev.tag || "—"} → ${next.tag || "—"}`);
  if (!!prev.questions !== !!next.questions) lines.push(`${path}: ${next.questions ? "flagged TBD" : "cleared TBD"}`);
  if (!!prev.pinned !== !!next.pinned) lines.push(`${path}: ${next.pinned ? "pinned" : "unpinned"}`);
  if ((prev.complexity || "none") !== (next.complexity || "none")) lines.push(`${path}: complexity ${prev.complexity || "none"} → ${next.complexity || "none"}`);
  if ((prev.comment || "") !== (next.comment || "")) lines.push(`${path}: comment ${next.comment ? (prev.comment ? "changed" : "added") : "removed"}`);
  listDiff(`${path}: dependency`, prev.links || [], next.links || [], lines); // 🔗 dependency chips
}

// Full plan diff. Returns [] when the snapshots are equivalent.
export function diffPlans(prev: PlanData | null | undefined, next: PlanData | null | undefined): string[] {
  const lines: string[] = [];
  const pName = (prev && prev.name) || "", nName = (next && next.name) || "";
  if (pName !== nName) lines.push(`Project renamed: "${pName}" → "${nName}"`);
  listDiff("Version", (prev && prev.versions) || [], (next && next.versions) || [], lines);
  listDiff("Component", (prev && prev.components) || [], (next && next.components) || [], lines);
  listDiff("Estimation", (prev && prev.estimations) || [], (next && next.estimations) || [], lines);
  if (!!(prev && prev.countDeps) !== !!(next && next.countDeps)) lines.push(`Setting: count dependencies ${next && next.countDeps ? "on" : "off"}`);
  if (!!(prev && prev.depDocOrder) !== !!(next && next.depDocOrder)) lines.push(`Setting: document-order dependencies ${next && next.depDocOrder ? "on" : "off"}`);
  if (!!(prev && prev.showComplexity) !== !!(next && next.showComplexity)) lines.push(`Setting: task complexity ${next && next.showComplexity ? "on" : "off"}`);

  const P = flatten((prev && prev.tree) || []);
  const N = flatten((next && next.tree) || []);
  for (const [id, cur] of N) {
    const old = P.get(id);
    if (!old) { lines.push(`Added: ${cur.path}`); continue; }
    if (old.node.name !== cur.node.name) lines.push(`Renamed: "${old.node.name}" → "${cur.node.name}"${cur.parent ? " (in " + cur.parent + ")" : ""}`);
    if (old.parent !== cur.parent) lines.push(`Moved "${cur.node.name}": ${old.parent || "(root)"} → ${cur.parent || "(root)"}`);
    fieldDiffs(old.node, cur.node, cur.path, lines);
  }
  for (const [id, old] of P) if (!N.has(id)) lines.push(`Removed: ${old.path}`);
  return lines;
}
